import { TransactionListHeaderComponent } from "../transaction-list-header.component";
import { Component, Input, OnInit } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";

import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { Fiat } from "@models";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { convertTradableToFiat, toCurrencyByFiatId } from "@helpers/tradable-convert";
import { AssetTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { Observable, take } from "rxjs";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";

@Component({
	selector: "asset-transaction-list-header",
	templateUrl: "./asset-transaction-list-header.component.html",
	standalone: false
})
export class AssetTransactionListHeaderComponent extends TransactionListHeaderComponent implements OnInit {
	@Input()
	public transaction: AssetTransaction = null;

	public isHideValueActive$: Observable<boolean> = this.portfolioFacade.isPortfolioDataHidden$;
	public showDestinationTag: boolean = this.transaction?.getDestinationTag() && !this.canOpen;
	public urlCheckAddress: string;

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected masterdataService: MasterdataService,
		protected payaccountService: PayAccountService,
		protected translationService: TranslationService,
		private readonly portfolioFacade: PortfolioFacade,
		private readonly cryptocoinsState: CryptocoinsState
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			masterdataService,
			payaccountService,
			translationService
		);
	}

	public ngOnInit(): void {
		this.setUrlCheckAddress();
	}

	public showRecipientLink(isStorageFee: boolean = true): boolean {
		if (isStorageFee) {
			return this.transaction.hasFees() && this.transaction.getType() !== "deposit" && this.storage_fee;
		}
		return this.transaction.hasFees() && this.transaction.getType() !== "deposit" && !this.storage_fee;
	}

	public isExternalAddress(): boolean {
		return this.showRecipientLink(false) && !this.isBestFeeTx();
	}

	public isRecipientBitpanda(): boolean {
		return this.isBestTradingFeeTx() || this.showRecipientLink();
	}

	public showBreakTagSection(): boolean {
		return (
			!this.transaction.hasFees() &&
			this.transaction.getType() !== "deposit" &&
			!this.isTrxCryptoMerger() &&
			!this.isTrxMarginTrading() &&
			!this.isTrxMarginTradingClose()
		);
	}

	public get metalName(): string {
		if (this.isBestMetalStorageFee()) {
			return this.tradableListService
				.getItemById(this.bfcEntity.getRelatedWalletTransaction().getCurrencyId())
				.getName();
		}
		return "";
	}

	public isBreakTagLink(): boolean {
		return this.transaction.isTransfer() && this.transaction.hasFrom() && !this.transaction.isOutgoing();
	}

	private get isStellarTag(): boolean {
		return this.tradable.isStellar() && this.transaction.getDtType().length > 0;
	}

	public getDestinationTagLabel(): string {
		if (this.isStellarTag) {
			return "stellar_" + this.transaction.getDtType();
		}

		return this.tradable.getSymbol(true) + "_destination_tag";
	}

	public getRelatedWallet(): TradableWallet {
		return this.tradableWalletListService.getItemById(this.transaction.getRelatedWalletId(), true);
	}

	public isSendProcessHeader(): boolean {
		return ["transfer", "deposit", "withdrawal", "send"].some(
			(txType: string) => txType === this.transaction.getType()
		);
	}

	public get amount(): number {
		if (this.transaction.getCurrentFiatId() === this.fiat.getId() && this.transaction.hasTrade()) {
			return +this.transaction.getCurrentFiatAmount();
		}

		if (!this.fiat) {
			return 0;
		}
		return +this.transaction.getAmountEurInklFee() / this.fiat.toEuroRate();
	}

	public getFiatAmountConverted(): number {
		return this.isExternalCryptoDepositWithFees()
			? toCurrencyByFiatId(+this.transaction.getAmountEuro(), this.fiat)
			: this.amount;
	}

	public getSpecialFeeFiat(): string {
		if (this.storage_fee || this.isBestMetalStorageFee()) {
			return "" + convertTradableToFiat(this.tradable, this.transaction.getFee(), this.fiat);
		}

		if (this.transaction.getIsBfc()) {
			// In case of a BEST-transaction, the price at the trading-time needs to be used
			// If the user has chosen a different base-currency than EUR, it needs be converted first
			const convertedBestPrice = toCurrencyByFiatId(
				this.transaction.getBestFeeCollection().getUsedBestPriceEur(),
				this.fiat
			);
			const amount = convertedBestPrice * this.transaction.getFee();
			return "" + amount;
		}
		return "";
	}

	public get fiatForAmount(): Fiat {
		return this.transaction.hasTrade()
			? this.fiatService.getItemById(this.transaction.getTrade().getFiatId())
			: this.fiat;
	}

	public breakTag(s: string): string {
		return s.replace("/", "<br>Tag: ");
	}

	public isSimpleInternalTx(): boolean {
		return !this.hasTrade() && !this.storage_fee && !this.isBestFeeTx();
	}

	public showStorageFee(isBest: boolean = false): boolean {
		if (this.hasTrade()) {
			return false;
		}
		return isBest ? this.isBestTradingFeeTx() : this.storage_fee;
	}

	public getInternalSendKey(): string {
		if (this.isOutgoing()) {
			return "sent_internal_send";
		}
		return "received_internal_send";
	}

	public get getTrxCryptoMergerToSymbol(): string {
		return this.transaction?.getRecipient().split(" ")[0];
	}

	public get getTrxCryptoMergerFromSymbol(): string {
		return this.transaction?.getFrom().split(" ")[0];
	}

	public get getTrxCryptoMergerDestination(): string {
		return this.transaction.isOutgoing() ? this.transaction.getFrom() : this.transaction.getRecipient();
	}

	public get geTrxMarginTradingDestination(): string {
		const tags = this.transaction.getTransactionTags();
		let walletName = "";
		tags.forEach((tag) => {
			if (
				(tag.attributes.short_name.includes("close") || tag.attributes.short_name.includes("fee")) &&
				(this.transaction.getType() === "transfer" || this.transaction.getType() === "internal_transfer")
			) {
				walletName = `${this.tradable.getSymbol()} ${this.translationService.translate("wallet")}`;
			}
		});
		return walletName;
	}

	public getTransferDisplayContent(): { type: string; content: string | null } {
		if (this.isTrxCryptoMerger()) {
			return {
				type: "cryptoMerger",
				content: this.getTrxCryptoMergerDestination
			};
		}
		if (
			(this.isTrxMarginTrading() || this.isTrxMarginTradingClose()) &&
			this.transaction.isIncoming() &&
			this.transaction.getType() !== "internal_transfer"
		) {
			return {
				type: "marginTrading",
				content: this.geTrxMarginTradingDestination
			};
		}
		return {
			type: "default",
			content: null // Handle in template
		};
	}

	public setUrlCheckAddress(): void {
		this.cryptocoinsState.cryptocoins.pipe(take(1)).subscribe((coins: PaymentsCryptocoin[]) => {
			const crypto: PaymentsCryptocoin = coins.find(
				({ cryptocoin_id }: PaymentsCryptocoin) => cryptocoin_id === +this.tradable.getId()
			);
			const network = crypto?.networks.find(
				({ coin_network_id }: Network) => coin_network_id === this.transaction.getCoinNetworkId()
			);
			const url: string = network?.url_check_address ?? this.tradable?.urlCheckAddress();
			this.urlCheckAddress = url.replace("%s", this.transaction?.getRecipient());
		});
	}
}
